<div class="container-fluid">
  <hc-page-heading [description]="'hotel_creation.subtitle' | translate">
    {{ 'hotel_creation.title' | translate }}
  </hc-page-heading>

  <!-- Pre-filled banner -->
  @if (prefillRequest) {
  <div class="alert alert-info d-flex align-items-center mb-4">
    <lucide-icon name="info" class="me-2 fs-5"></lucide-icon>
    {{ 'hotel_creation.prefilled_banner' | translate:{ id: onboardingRequestId } }}
  </div>
  }

  <form [formGroup]="hotelForm" (ngSubmit)="onSubmit()" autocomplete="off">
    <div class="row">
      <!-- Left Column: Hotel Identity -->
      <div class="col-lg-6">
        <div class="card card-v2 mb-4">
          <div class="card-body">
            <div class="form-section">
              <div class="form-section-header">
                <div class="form-section-icon">
                  <lucide-icon name="building-2"></lucide-icon>
                </div>
                <h5 class="form-section-title">{{ 'hotel_creation.section_identity' | translate }}</h5>
              </div>
            <div class="row g-3">
              <!-- Hotel Name -->
              <div class="col-12">
                <hc-form-field [label]="'hotel_info.hotel_name' | translate" [required]="true" [error]="getError('name')">
                  <hc-input formControlName="name" [placeholder]="'placeholders.enter_hotel_name' | translate" [hasError]="!!getError('name')"></hc-input>
                </hc-form-field>
              </div>

              <!-- Rating + Superior (nested row so Country/Region/Currency start on a new row) -->
              <div class="col-12">
                <div class="row g-3 align-items-end">
                  <div class="col">
                    <hc-form-field [label]="'hotel_info.hotel_category' | translate" [required]="true" [error]="getError('rating')">
                      <hc-input-dropdown formControlName="rating"
                        [options]="ratingDropdownOptions"
                        [placeholder]="'common.select' | translate"
                        [hasError]="!!getError('rating')">
                      </hc-input-dropdown>
                    </hc-form-field>
                  </div>
                  <div class="col-auto">
                    <hc-form-field controlType="checkbox">
                      <hc-toggle
                        [label]="'hotel_info.superior' | translate"
                        formControlName="superior_hotel">
                      </hc-toggle>
                    </hc-form-field>
                  </div>
                </div>
              </div>

              <!-- Country + Region + Currency (wrapped in col-12 to prevent ZIP from joining this row) -->
              <div class="col-12">
                <div class="row g-3 align-items-start">
                  <!-- Country -->
                  <div class="col-md">
                    <hc-form-field
                      [label]="'hotel_info.country' | translate"
                      [required]="true"
                      [error]="getError('country_id')">
                      <ng-select formControlName="country_id"
                        [class.is-invalid]="f['country_id'].invalid && f['country_id'].touched"
                        [items]="countries"
                        bindValue="id"
                        bindLabel="name"
                        [clearable]="false"
                        [placeholder]="'common.select' | translate">
                      </ng-select>
                    </hc-form-field>
                  </div>

                  <!-- Region -->
                  <div class="col-md">
                    <hc-form-field
                      [label]="'hotel_info.region' | translate"
                      [publishRequired]="!f['region_id'].value"
                      [error]="getError('region_id')">
                      <ng-select formControlName="region_id"
                        [class.is-invalid]="f['region_id'].invalid && f['region_id'].touched"
                        [items]="regions"
                        bindValue="id"
                        bindLabel="name"
                        [clearable]="false"
                        [placeholder]="'common.select' | translate">
                      </ng-select>
                    </hc-form-field>
                  </div>

                  <!-- Currency -->
                  <div class="col-auto">
                    <hc-form-field [label]="'hotel_info.currency' | translate" [required]="true" controlType="radio">
                      <hc-radio-group [options]="currencyOptions" formControlName="currency" name="currency"></hc-radio-group>
                    </hc-form-field>
                  </div>
                </div>
              </div>

              <!-- ZIP / Town -->
              <div class="col-md-6">
                <hc-form-field [label]="'hotel_info.zip_code' | translate" [required]="true" [error]="getError('postal_code')">
                  <hc-input formControlName="postal_code" [placeholder]="'placeholders.enter_postal_code' | translate" [hasError]="!!getError('postal_code')"></hc-input>
                </hc-form-field>
              </div>

              <div class="col-md-6">
                <hc-form-field [label]="'hotel_info.town' | translate" [required]="true" [error]="getError('city')">
                  <hc-input formControlName="city" [placeholder]="'placeholders.enter_city' | translate" [hasError]="!!getError('city')"></hc-input>
                </hc-form-field>
              </div>

              <!-- Address -->
              <div class="col-md-8">
                <hc-form-field [label]="'hotel_info.street' | translate" [error]="getError('street')">
                  <hc-input formControlName="street" [placeholder]="'placeholders.enter_street' | translate" [hasError]="!!getError('street')"></hc-input>
                </hc-form-field>
              </div>

              <!-- House Number -->
              <div class="col-md-4">
                <hc-form-field [label]="'hotel_info.house_number' | translate"
                               [required]="true"
                               [error]="getError('number')">
                  <hc-input formControlName="number" [placeholder]="'placeholders.enter_house_number' | translate" [hasError]="!!getError('number')"></hc-input>
                </hc-form-field>
              </div>


            </div>
            </div><!-- /form-section -->
          </div>
        </div>
      </div>

      <!-- Right Column: Primary Contact -->
      <div class="col-lg-6">
        <div class="card card-v2 mb-4">
          <div class="card-body">
            <div class="form-section">
              <div class="form-section-header">
                <div class="form-section-icon">
                  <lucide-icon name="user"></lucide-icon>
                </div>
                <h5 class="form-section-title">{{ 'hotel_creation.section_contact' | translate }}</h5>
              </div>
            <div class="row g-3">
              <!-- Salutation -->
              <div class="col-12">
                <hc-form-field [label]="'contact_info.salutation' | translate" [required]="true" [error]="getError('salutation')">
                  <hc-radio-group [options]="salutationOptions" formControlName="salutation" name="salutation"></hc-radio-group>
                </hc-form-field>
              </div>

              <!-- First Name -->
              <div class="col-md-6">
                <hc-form-field [label]="'contact_info.first_name' | translate" [required]="true" [error]="getError('first_name')">
                  <hc-input formControlName="first_name" [placeholder]="'placeholders.enter_first_name' | translate" [hasError]="!!getError('first_name')"></hc-input>
                </hc-form-field>
              </div>

              <!-- Last Name -->
              <div class="col-md-6">
                <hc-form-field [label]="'contact_info.last_name' | translate" [required]="true" [error]="getError('last_name')">
                  <hc-input formControlName="last_name" [placeholder]="'placeholders.enter_last_name' | translate" [hasError]="!!getError('last_name')"></hc-input>
                </hc-form-field>
              </div>

              <!-- Role -->
              <div class="col-md-6">
                <hc-form-field [label]="'contact_info.role' | translate" [error]="getError('role')">
                  <hc-input formControlName="role" [placeholder]="'placeholders.enter_role' | translate" [hasError]="!!getError('role')"></hc-input>
                </hc-form-field>
              </div>

              <!-- Email -->
              <div class="col-md-6">
                <hc-form-field [label]="'contact_info.email' | translate" [required]="true" [error]="getError('email')">
                  <hc-input type="email" formControlName="email" [placeholder]="'placeholders.enter_email' | translate" [hasError]="!!getError('email')"></hc-input>
                </hc-form-field>
              </div>

              <!-- Phone Number -->
              <div class="col-md-6">
                <hc-form-field [label]="'contact_info.phone' | translate" [required]="true" [error]="getError('phone_number')">
                  <hc-input type="tel" formControlName="phone_number" [placeholder]="'placeholders.enter_phone' | translate" [hasError]="!!getError('phone_number')"></hc-input>
                </hc-form-field>
              </div>

              <!-- Hotel Phone Number -->
              <div class="col-md-6">
                <hc-form-field [label]="'contact_info.hotel_phone' | translate" [required]="true" [error]="getError('hotel_phone_number')">
                  <hc-input type="tel" formControlName="hotel_phone_number" [placeholder]="'placeholders.enter_hotel_phone' | translate" [hasError]="!!getError('hotel_phone_number')"></hc-input>
                </hc-form-field>
              </div>

              <!-- Booking Email -->
              <div class="col-md-6">
                <hc-form-field [label]="'contact_info.booking_email' | translate" [required]="true" [error]="getError('booking_email')">
                  <hc-input type="email" formControlName="booking_email" [placeholder]="'placeholders.enter_booking_email' | translate" [hasError]="!!getError('booking_email')"></hc-input>
                </hc-form-field>
              </div>

              <!-- General Email -->
              <div class="col-md-6">
                <hc-form-field [label]="'contact_info.general_email' | translate" [error]="getError('general_email')">
                  <hc-input type="email" formControlName="general_email" [placeholder]="'placeholders.enter_general_email' | translate" [hasError]="!!getError('general_email')"></hc-input>
                </hc-form-field>
              </div>

              <!-- Website -->
              <div class="col-12">
                <hc-form-field [label]="'contact_info.website' | translate" [error]="getError('website')">
                  <hc-input formControlName="website" [placeholder]="'placeholders.enter_website' | translate" [hasError]="!!getError('website')"></hc-input>
                </hc-form-field>
              </div>
            </div>
            </div><!-- /form-section -->
          </div>
        </div>
      </div>
    </div>


    <!-- Submit Button -->
    <div class="d-flex justify-content-end gap-2 mb-4">
      <hc-button
        variant="primary"
        type="submit"
        size="md"
        [disabled]="isSubmitting"
        [loading]="isSubmitting">
        {{ isSubmitting ? ('hotel_creation.creating' | translate) : ('hotel_creation.title' | translate) }}
      </hc-button>
    </div>
  </form>
</div>
